import type { ReactNode } from "react";
import type { StepExecutionItem } from "../../shared/executions/executionListTypes";
import { useFormatRelativeTime } from "../hooks/useFormatRelativeTime";
import { useMakeUrl } from "../lib/urls";
import { Dropdown } from "./Dropdown";
import { ExecutionSidebar, ExecutionSidebarRow } from "./ExecutionSidebar";
import { HeadingDropdownOpener } from "./HeadingDropdownOpener";
import { Icon } from "./Icon";
import { MetaSep } from "./MetaSeparator";
import { OpenerLabel, pinPanelSection, sidePanelWidth } from "./PinnedPanel";
import { formatTime } from "./StepExecutions";
import { ExecutionNav } from "./StepExecutionNav";
import { Text } from "./Text";
import { Tooltip } from "./Tooltip";
import { WavyLine } from "./WavyLine";

function listedExecutions(
  executions: StepExecutionItem[],
  selectedExecution: StepExecutionItem | undefined
) {
  // Failure cycling can select a run outside the recent execution window.
  const items =
    selectedExecution &&
    !executions.some((item) => item.id === selectedExecution.id)
      ? [...executions, selectedExecution].sort(
          (a, b) => b.createdAt - a.createdAt
        )
      : executions;
  const pendingBuildKeys = new Set(
    items
      .filter((item) => item.kind === "build" && item.status === "pending")
      .map((item) => item.buildKey)
  );
  const isQueued = (execution: StepExecutionItem) =>
    execution.kind === "run" &&
    execution.status === "pending" &&
    pendingBuildKeys.has(execution.buildKey);
  return { items, isQueued };
}

function executionLabel(execution: StepExecutionItem) {
  if (execution.kind === "build" && execution.status === "pending")
    return "Building…";
  return execution.kind === "build"
    ? (execution.run?.commit?.message ?? formatTime(execution.createdAt))
    : formatTime(execution.createdAt);
}

function ExecutionIcon({
  execution,
  queued,
  pinned,
  size = 16,
}: {
  execution: StepExecutionItem;
  queued: boolean;
  pinned: boolean;
  size?: 12 | 16;
}) {
  if (execution.kind === "build" && execution.status === "pending")
    return <WavyLine size={size} opacity={1} />;
  if (execution.kind === "build")
    return (
      <Tooltip content="Build">
        <Icon name="package-2" size={size} />
      </Tooltip>
    );
  if (queued) return <Icon name="clock" size={size} />;
  if (pinned)
    return (
      <Tooltip content="Step test">
        <Icon name="microscope" size={size} />
      </Tooltip>
    );
  return (
    <Icon
      name={execution.run?.ephemeral ? "solo-run" : "run"}
      size={execution.run?.ephemeral ? size : 12}
    />
  );
}

function ExecutionStatus({
  execution,
  queued,
}: {
  execution: StepExecutionItem;
  queued: boolean;
}) {
  const formatRelativeTime = useFormatRelativeTime();
  return (
    <>
      <span
        style={{
          color:
            execution.status === "success"
              ? "var(--green-600)"
              : execution.status === "error"
                ? "var(--red-600)"
                : undefined,
        }}
      >
        {queued
          ? "Queued"
          : execution.status === "pending"
            ? "Running"
            : execution.status === "success"
              ? "Successful"
              : "Failed"}
      </span>
      <MetaSep />
      {formatRelativeTime(execution.createdAt)}
    </>
  );
}

export function StepExecutionSidebar({
  executions,
  selectedExecution,
  loading,
  pinnedByExecId,
  workflowId,
  branchId,
  stepDir,
  onNavigate,
  onCollapse,
  action,
}: {
  executions: StepExecutionItem[];
  selectedExecution: StepExecutionItem | undefined;
  loading: boolean;
  pinnedByExecId: ReadonlyMap<string, string>;
  workflowId: string;
  branchId: string;
  stepDir: string;
  onNavigate?: () => void;
  onCollapse: () => void;
  action?: ReactNode;
}) {
  const makeUrl = useMakeUrl();
  const selectedId = selectedExecution?.id;
  const { items, isQueued } = listedExecutions(executions, selectedExecution);
  return (
    <ExecutionSidebar
      selectedId={selectedId}
      loading={loading}
      empty={items.length === 0}
      onCollapse={onCollapse}
      onNavigate={onNavigate}
      actions={
        <>
          {action}
          {selectedExecution && (
            <ExecutionNav
              executions={executions}
              selectedExecution={selectedExecution}
              workflowId={workflowId}
              branchId={branchId}
              stepDir={stepDir}
            />
          )}
        </>
      }
    >
      {items.map((execution) => (
        <ExecutionSidebarRow
          key={execution.id}
          to={makeUrl(null, { execution: execution.id })}
          onNavigate={onNavigate}
          selected={execution.id === selectedId}
          label={executionLabel(execution)}
          icon={
            <ExecutionIcon
              execution={execution}
              queued={isQueued(execution)}
              pinned={pinnedByExecId.has(execution.id)}
            />
          }
        >
          <ExecutionStatus execution={execution} queued={isQueued(execution)} />
        </ExecutionSidebarRow>
      ))}
    </ExecutionSidebar>
  );
}

export function StepExecutionPicker({
  executions,
  selectedExecution,
  pinnedByExecId,
  pinned,
  onTogglePinned,
  onNavigate,
}: {
  executions: StepExecutionItem[];
  selectedExecution: StepExecutionItem | undefined;
  pinnedByExecId: ReadonlyMap<string, string>;
  pinned: boolean;
  onTogglePinned: () => void;
  onNavigate?: () => void;
}) {
  const makeUrl = useMakeUrl();
  const { items, isQueued } = listedExecutions(executions, selectedExecution);
  return (
    <Dropdown
      maxHeight={400}
      minWidth={sidePanelWidth}
      maxWidth={sidePanelWidth}
      opener={
        <HeadingDropdownOpener aria-label="Executions">
          {selectedExecution ? (
            <>
              <ExecutionIcon
                execution={selectedExecution}
                queued={isQueued(selectedExecution)}
                pinned={pinnedByExecId.has(selectedExecution.id)}
                size={12}
              />
              <OpenerLabel>
                <Text variant="text-small-md" ellipsis>
                  {executionLabel(selectedExecution)}
                </Text>
              </OpenerLabel>
            </>
          ) : (
            <>
              <Icon name="history" size={12} />
              <Text variant="text-small-md">Executions</Text>
            </>
          )}
          <Icon name="chevron-down" size={12} />
        </HeadingDropdownOpener>
      }
      sections={[
        {
          label: "Executions",
          items: items.map((execution) => ({
            type: "link" as const,
            compact: true,
            key: execution.id,
            to: makeUrl(null, { execution: execution.id }),
            onClick: onNavigate,
            label: executionLabel(execution),
            subtitle: (
              <Text variant="text-small" color="var(--text-secondary)">
                <ExecutionStatus
                  execution={execution}
                  queued={isQueued(execution)}
                />
              </Text>
            ),
            left: (
              <ExecutionIcon
                execution={execution}
                queued={isQueued(execution)}
                pinned={pinnedByExecId.has(execution.id)}
              />
            ),
            selected: execution.id === selectedExecution?.id,
            right:
              execution.id === selectedExecution?.id ? (
                <Icon name="check" size={12} color="var(--text-secondary)" />
              ) : undefined,
          })),
        },
        pinPanelSection({
          label: "Pin executions panel",
          side: "right",
          pinned,
          onToggle: onTogglePinned,
        }),
      ]}
    />
  );
}
